// B21 — daily buy box agents: shared limits, entitlement and listing-key rules
// (used by the server job/routes and the client settings card).

import { resolveV3State } from "./billing-entitlement";
import { hasUnlimitedSearch, type AccountType } from "./schema";

/** Banner + proof bar: "Up to 3 daily buy box agents". */
export const MAX_BUY_BOX_AGENTS = 3;
/** Local hour the daily run fires (account timezone). */
export const BUY_BOX_AGENT_RUN_HOUR = 6;
/** In-app "New matches" list window. */
export const BUY_BOX_MATCH_LIST_DAYS = 30;
/** Top N houses in the daily email. */
export const BUY_BOX_EMAIL_TOP_N = 10;
/** Default admin daily cost alert threshold, cents ($25/day). */
export const DEFAULT_BUY_BOX_DAILY_COST_ALERT_CENTS = 2500;

export const BUY_BOX_AGENT_UPSELL = "Daily buy box alerts are included with Host.";

export interface BuyBoxAgentEntitlementInput {
  billingModel: string | null;
  signupRole?: string | null;
  selectedPlan?: string | null;
  subscriptionStatus: string | null;
  trialEndsAt: Date | string | null;
  pausedAt: Date | string | null;
  pastDueSince: Date | string | null;
  lifetimeAllowance: number | null;
}

export type BuyBoxAgentEntitlement = { eligible: true } | { eligible: false; reason: "research" | "inactive" };

/**
 * Host and Property Manager accounts that are trialing, active, lifetime or
 * comped. Free Research accounts (and paused / past-due / lapsed accounts)
 * can't run agents.
 */
export function buyBoxAgentEntitlement(owner: BuyBoxAgentEntitlementInput, now: Date = new Date()): BuyBoxAgentEntitlement {
  if (owner.billingModel === "v3") {
    const { state } = resolveV3State({
      subscriptionStatus: owner.subscriptionStatus,
      trialEndsAt: owner.trialEndsAt,
      pausedAt: owner.pausedAt,
      pastDueSince: owner.pastDueSince,
      lifetimeAllowance: owner.lifetimeAllowance,
      now,
    });
    if (state === "research") return { eligible: false, reason: "research" };
    if (state === "trialing" || state === "active" || state === "lifetime" || state === "comped") return { eligible: true };
    return { eligible: false, reason: "inactive" };
  }
  // Legacy (pre-v3) plans: the same "unlimited search" rule the manual buy box uses.
  const plan = (owner.selectedPlan ?? "Research") as AccountType;
  if (hasUnlimitedSearch({ plan, subscriptionStatus: owner.subscriptionStatus })) return { eligible: true };
  return { eligible: false, reason: plan === "Research" ? "research" : "inactive" };
}

/**
 * Stable per-listing key so the same house is never sent twice: the source
 * listing id when present, else the listing URL, else a normalized address.
 */
export function buyBoxListingKey(row: { listingId?: string | null; listingUrl?: string | null; address?: string | null; city?: string | null; state?: string | null; zipCode?: string | null }): string | null {
  const id = (row.listingId ?? "").trim();
  if (id) return `id:${id}`;
  const url = (row.listingUrl ?? "").trim();
  if (url) {
    try {
      const u = new URL(url);
      return `url:${u.hostname.replace(/^www\./, "").toLowerCase()}${u.pathname.replace(/\/+$/, "").toLowerCase()}`;
    } catch {
      return `url:${url.toLowerCase()}`;
    }
  }
  const addr = [row.address, row.city, row.state, row.zipCode]
    .map((p) => (p ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim())
    .filter(Boolean)
    .join("|");
  return addr ? `addr:${addr}` : null;
}

/** True once the account-local clock has reached the daily run hour. */
export function isAgentRunDue(localHour: number): boolean {
  return localHour >= BUY_BOX_AGENT_RUN_HOUR;
}

// ── Buy Box Agents with versions ─────────────────────────────────────────

/** "New listings" lookback windows an agent can use (days). */
export const BUY_BOX_AGENT_LOOKBACK_OPTIONS = [1, 2, 7] as const;
export type BuyBoxAgentLookbackDays = typeof BUY_BOX_AGENT_LOOKBACK_OPTIONS[number];
export const DEFAULT_BUY_BOX_AGENT_LOOKBACK_DAYS: BuyBoxAgentLookbackDays = 2;

export function isBuyBoxAgentLookbackDays(n: unknown): n is BuyBoxAgentLookbackDays {
  return typeof n === "number" && (BUY_BOX_AGENT_LOOKBACK_OPTIONS as readonly number[]).includes(n);
}

/** Every criteria field a Buy Box version holds (underwriting stays account-wide). */
export const BUY_BOX_VERSION_CRITERIA_KEYS = [
  "maxPurchasePrice",
  "minBedrooms",
  "maxBedrooms",
  "minSqft",
  "maxSqft",
  "minLotSize",
  "minCocReturn",
  "minMonthlyCashFlow",
  "noHoa",
] as const;
export type BuyBoxVersionCriteriaKey = typeof BUY_BOX_VERSION_CRITERIA_KEYS[number];

/** "V2" or "V2 (Atlanta starter)". */
export function buyBoxVersionLabel(versionNumber: number | null | undefined, label?: string | null): string {
  if (versionNumber == null) return "Buy Box";
  const l = (label ?? "").trim();
  return l ? `V${versionNumber} (${l})` : `V${versionNumber}`;
}

/**
 * The agreed run name: `Agent 2 · Buy Box V1 · Las Vegas, NV · 2026-06-12`,
 * using the account-local run date.
 */
export function buyBoxAgentRunName(p: { agentNumber: number | null; versionNumber: number | null; city: string; state: string; runDate: string }): string {
  const agent = p.agentNumber != null ? `Agent ${p.agentNumber}` : "Agent";
  const version = p.versionNumber != null ? `Buy Box V${p.versionNumber}` : "Buy Box";
  return `${agent} · ${version} · ${p.city}, ${p.state} · ${p.runDate}`;
}

/** Short tag shown on a property card: `Agent 2 · V1 · Las Vegas, NV`. */
export function buyBoxAgentTagText(t: { agentNumber: number | null; versionNumber: number | null; city: string; state: string }): string {
  const agent = t.agentNumber != null ? `Agent ${t.agentNumber}` : "Agent";
  const version = t.versionNumber != null ? `V${t.versionNumber}` : "Buy Box";
  return `${agent} · ${version} · ${t.city}, ${t.state}`;
}

/** Lowest agent number (1…) not already used by the account's agents. */
export function nextFreeAgentNumber(used: Array<number | null | undefined>): number {
  const taken = new Set(used.filter((n): n is number => typeof n === "number"));
  let n = 1;
  while (taken.has(n)) n++;
  return n;
}

/**
 * Days-on-Zillow bucket for a lookback window. Zillow only offers fixed
 * buckets, so 2 days uses the 7-day bucket and the scan trims to the exact
 * window afterwards.
 */
export function zillowDaysOnBucket(lookbackDays: number): "1" | "7" | "14" | "30" {
  if (lookbackDays <= 1) return "1";
  if (lookbackDays <= 7) return "7";
  if (lookbackDays <= 14) return "14";
  return "30";
}

/**
 * True when the scraper surfaced a usable listing age (listing date or
 * days-on-Zillow). Listings without one cannot be trimmed to the exact
 * lookback window, so agent runs and the live smoke check report them.
 */
export function hasListingAge(p: { days_on_market?: number | null; listed_at?: string | null }): boolean {
  if (p.listed_at && Number.isFinite(Date.parse(p.listed_at))) return true;
  return typeof p.days_on_market === "number" && Number.isFinite(p.days_on_market);
}

/**
 * Exact-window trim for a scraped listing. Keeps it when the listing date or
 * days-on-Zillow show it was listed inside the window; keeps it too when the
 * scraper returned neither (the Zillow bucket already bounded the search).
 */
export function listingWithinLookback(
  p: { days_on_market?: number | null; listed_at?: string | null },
  lookbackDays: number,
  now: Date = new Date(),
): boolean {
  if (p.listed_at) {
    const t = Date.parse(p.listed_at);
    if (Number.isFinite(t)) return now.getTime() - t <= lookbackDays * 86_400_000;
  }
  if (typeof p.days_on_market === "number" && Number.isFinite(p.days_on_market)) {
    // Zillow's daysOnZillow is whole days since listing (0 = under 24h ago).
    return p.days_on_market < lookbackDays;
  }
  return true;
}
